/**
 * CaregiverProfilePage (CONTRACTS §6): role-appropriate profile edit for
 * the caregiver — display name, language and timezone via
 * repositories.profile.upsertMyProfile. E-mail address is read-only
 * identity data from the session.
 */
import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Select, Skeleton, TextField, useToast } from '@/components/ui'
import { repositories } from '@/services/repositories'
import { useAuth } from '@/features/auth'
import { errorMessage } from '@/features/medication'
import './caregiver.css'

const LOCALE_OPTIONS = [
  { value: 'nl-NL', label: 'Nederlands (Nederland)' },
  { value: 'nl-BE', label: 'Nederlands (België)' },
]

function timezoneOptions(current: string): { value: string; label: string }[] {
  const known = ['Europe/Amsterdam', 'Europe/Brussels', 'Europe/London', 'UTC']
  const all = current && !known.includes(current) ? [current, ...known] : known
  return all.map((zone) => ({ value: zone, label: zone.replace('_', ' ') }))
}

export default function CaregiverProfilePage() {
  const { profile, email, refreshProfile } = useAuth()
  const { showToast } = useToast()

  const [displayName, setDisplayName] = useState('')
  const [locale, setLocale] = useState('nl-NL')
  const [timezone, setTimezone] = useState('Europe/Amsterdam')
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    if (profile && !loaded) {
      setDisplayName(profile.displayName ?? '')
      setLocale(profile.locale || 'nl-NL')
      setTimezone(profile.timezone || 'Europe/Amsterdam')
      setLoaded(true)
    }
  }, [profile, loaded])

  const zones = useMemo(() => timezoneOptions(timezone), [timezone])

  const dirty =
    loaded &&
    profile !== null &&
    (displayName !== (profile.displayName ?? '') ||
      locale !== (profile.locale || 'nl-NL') ||
      timezone !== (profile.timezone || 'Europe/Amsterdam'))

  const save = async () => {
    if (saving) return
    if (!displayName.trim()) {
      setNameError('Vul je naam in.')
      return
    }
    setNameError(null)
    setSaving(true)
    try {
      await repositories.profile.upsertMyProfile({
        displayName: displayName.trim(),
        locale,
        timezone,
      })
      await refreshProfile()
      showToast({ message: 'Profiel opgeslagen.', tone: 'success' })
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) {
    return (
      <div className="caregiver-page">
        <h1 className="caregiver-section-title">Mijn profiel</h1>
        <Skeleton lines={3} />
      </div>
    )
  }

  return (
    <div className="caregiver-page">
      <header>
        <h1 className="caregiver-section-title">Mijn profiel</h1>
        <p className="caregiver-muted">
          Deze gegevens zijn zichtbaar voor je gekoppelde cliënten.
        </p>
      </header>

      <Card padding="md">
        <div className="caregiver-form">
          <TextField
            label="E-mailadres"
            value={email ?? ''}
            onChange={() => undefined}
            disabled
            hint="Je e-mailadres kan niet worden gewijzigd."
          />
          <TextField
            label="Weergavenaam"
            value={displayName}
            onChange={setDisplayName}
            error={nameError ?? undefined}
            required
            autoComplete="name"
          />
          <Select label="Taal" value={locale} onChange={setLocale} options={LOCALE_OPTIONS} />
          <Select label="Tijdzone" value={timezone} onChange={setTimezone} options={zones} />
          <div className="caregiver-actions">
            <Button variant="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
              Wijzigingen opslaan
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
